import type { ApiResult, RequestOptions } from '@/services/apiClient';
import { apiClient, apiOrigin } from '@/services/apiClient';
import type { ProjectMapResponse } from '@/services/realtimeClient';
import { scopePortfolioMap } from './portfolioMapScope';
import type { ProjectPortfolioEvidence } from './portfolioEvidence.types';
import type {
  ArchiveProjectResult,
  ProjectActionInput,
  ProjectDetail,
  ProjectFilters,
  ProjectInput,
  ProjectPage,
  ProjectPortfolioFilters,
  ProjectPortfolioSummary,
  ProjectTimeline,
  UpdateProjectInput,
} from './projects.types';

const PROJECTS_PATH = `${apiOrigin}/api/v1/projects/projects`;

export class ProjectApiError extends Error {
  constructor(
    message: string,
    readonly status: number,
  ) {
    super(message);
    this.name = 'ProjectApiError';
  }

  get isOffline(): boolean {
    return this.status === 0;
  }
}

function expectData<T>(result: ApiResult<T>, fallback: string): T {
  if (!result.ok || result.data === null) {
    throw new ProjectApiError(result.error ?? fallback, result.status);
  }
  return result.data;
}

export function projectQueryString(filters: ProjectFilters): string {
  const params = new URLSearchParams({
    page: String(filters.page),
    pageSize: String(filters.pageSize),
    sortBy: filters.sortBy,
    sortOrder: filters.sortOrder,
  });

  if (filters.search.trim()) params.set('search', filters.search.trim());
  if (filters.status) params.set('status', filters.status);
  if (filters.phase) params.set('phase', filters.phase);
  if (filters.riskLevel) params.set('riskLevel', filters.riskLevel);
  if (filters.sector.trim()) params.set('sector', filters.sector.trim());
  if (filters.projectId) params.set('projectId', filters.projectId);
  if (filters.regionId) params.set('regionId', filters.regionId);
  if (filters.cohort) params.set('cohort', filters.cohort);

  return `?${params.toString()}`;
}

export function portfolioQueryString(filters: ProjectPortfolioFilters): string {
  const params = new URLSearchParams();
  for (const key of [
    'search',
    'status',
    'phase',
    'riskLevel',
    'sector',
    'projectId',
    'regionId',
    'cohort',
  ] as const) {
    const value = filters[key]?.trim();
    if (value) params.set(key, value);
  }
  return params.size ? `?${params.toString()}` : '';
}

async function portfolioSummary(
  filters: ProjectPortfolioFilters,
  options?: RequestOptions,
): Promise<ProjectPortfolioSummary> {
  return expectData(
    await apiClient.get<ProjectPortfolioSummary>(
      `${PROJECTS_PATH}/summary${portfolioQueryString(filters)}`,
      options,
    ),
    'Portfolio summary could not be loaded',
  );
}

async function list(filters: ProjectFilters, options?: RequestOptions): Promise<ProjectPage> {
  return expectData(
    await apiClient.get<ProjectPage>(`${PROJECTS_PATH}${projectQueryString(filters)}`, options),
    'Projects could not be loaded',
  );
}

async function portfolioEvidence(
  filters: ProjectPortfolioFilters,
  options?: RequestOptions,
): Promise<ProjectPortfolioEvidence> {
  return expectData(
    await apiClient.get<ProjectPortfolioEvidence>(
      `${PROJECTS_PATH}/evidence${portfolioQueryString(filters)}`,
      options,
    ),
    'Portfolio source evidence could not be loaded',
  );
}

/** Every bounded page must reconcile before these options are called complete. */
async function listAllScopes(
  filters: ProjectFilters,
  options?: RequestOptions,
): Promise<ProjectPage> {
  const first = await list({ ...filters, page: 1, pageSize: 100 }, options);
  const items = [...first.items];
  for (let page = 2; page <= first.meta.totalPages; page++) {
    options?.signal?.throwIfAborted();
    const next = await list({ ...filters, page, pageSize: 100 }, options);
    if (next.meta.total !== first.meta.total || next.meta.totalPages !== first.meta.totalPages) {
      throw new ProjectApiError(
        'Project options changed while loading. Refresh to load the current register.',
        409,
      );
    }
    items.push(...next.items);
  }
  if (
    items.length !== first.meta.total ||
    new Set(items.map((item) => item.id)).size !== items.length
  ) {
    throw new ProjectApiError('Project options are incomplete. Refresh to retry.', 409);
  }
  return { items, meta: { ...first.meta, page: 1, totalPages: items.length ? 1 : 0 } };
}

async function get(id: string, options?: RequestOptions): Promise<ProjectDetail> {
  return expectData(
    await apiClient.get<ProjectDetail>(`${PROJECTS_PATH}/${encodeURIComponent(id)}`, options),
    'Project could not be loaded',
  );
}

async function portfolioMap(filters: ProjectFilters, options?: RequestOptions) {
  const membership = {
    ...filters,
    page: 1,
    pageSize: 100,
    sortBy: 'name' as const,
    sortOrder: 'asc' as const,
  };
  const before = await portfolioSummary(membership, options);
  const [cohort, snapshot] = await Promise.all([
    listAllScopes(membership, options),
    apiClient
      .get<ProjectMapResponse>(`${apiOrigin}/api/v1/projects/map`, options)
      .then((result) =>
        expectData(result, 'The current project-map permission and source are required.'),
      ),
  ]);
  options?.signal?.throwIfAborted();
  const after = await portfolioSummary(membership, options);
  return scopePortfolioMap(cohort, snapshot, before, after);
}

async function create(input: ProjectInput, options?: RequestOptions): Promise<ProjectDetail> {
  return expectData(
    await apiClient.post<ProjectDetail, ProjectInput>(PROJECTS_PATH, input, options),
    'Project could not be created',
  );
}

async function update(
  id: string,
  input: UpdateProjectInput,
  options?: RequestOptions,
): Promise<ProjectDetail> {
  return expectData(
    await apiClient.patch<ProjectDetail, UpdateProjectInput>(
      `${PROJECTS_PATH}/${encodeURIComponent(id)}`,
      input,
      options,
    ),
    'Project could not be updated',
  );
}

async function archive(
  id: string,
  version: number,
  options?: RequestOptions,
): Promise<ArchiveProjectResult> {
  return expectData(
    await apiClient.delete<ArchiveProjectResult>(
      `${PROJECTS_PATH}/${encodeURIComponent(id)}?version=${version}`,
      options,
    ),
    'Project could not be archived',
  );
}

async function runAction(
  id: string,
  input: ProjectActionInput,
  options?: RequestOptions,
): Promise<ProjectDetail> {
  return expectData(
    await apiClient.post<ProjectDetail, ProjectActionInput>(
      `${PROJECTS_PATH}/${encodeURIComponent(id)}/actions`,
      input,
      options,
    ),
    'Project action could not be completed',
  );
}

async function timeline(id: string, options?: RequestOptions): Promise<ProjectTimeline> {
  return expectData(
    await apiClient.get<ProjectTimeline>(
      `${PROJECTS_PATH}/${encodeURIComponent(id)}/timeline`,
      options,
    ),
    'Project timeline could not be loaded',
  );
}

export const projectsClient = {
  list,
  listAllScopes,
  portfolioSummary,
  portfolioEvidence,
  portfolioMap,
  get,
  create,
  update,
  archive,
  runAction,
  timeline,
};
